import { Button } from '@magicschool/spellbook/components/Button';
import type { ButtonGroupCollapsedItem } from '@magicschool/spellbook/components/ButtonGroup/slots';
import { DropdownMenu, type DropdownMenuSubProps } from '@magicschool/spellbook/components/DropdownMenu';
import type { IconProps } from '@magicschool/spellbook/components/Icon';
import { IconButton } from '@magicschool/spellbook/components/IconButton';
import type { ComponentType, ReactElement, ReactNode } from 'react';
import { Children, isValidElement } from 'react';

export type CollapsedAction = {
  label: string;
  icon?: IconProps['name'];
  onSelect?: () => void;
  disabled?: boolean;
  subItems?: ButtonGroupCollapsedItem[];
  submenuProps?: Partial<Omit<DropdownMenuSubProps, 'children'>>;
  /** Position of this action in the collapsed menu, lowest first. `undefined` when the slot didn't set `collapsedOrder` — callers fall back to row order. */
  order?: number;
};

/** Depth-first search for the first element of one of `types` in `node` (which may itself be a list). */
export function findFirstElement(node: ReactNode, types: readonly ComponentType[]): ReactElement | null {
  for (const child of Children.toArray(node)) {
    if (!isValidElement(child)) continue;
    if (types.includes(child.type as ComponentType)) return child;

    const found = findFirstElement((child.props as { children?: ReactNode })?.children, types);
    if (found) return found;
  }

  return null;
}

/** Derives the collapsed menu-item representation of a `ButtonGroup.Button` slot's props, or `null` if it has no usable label. */
export function getCollapsedAction(slotProps: Record<string, unknown>): CollapsedAction | null {
  const collapsedLabel = slotProps.collapsedLabel as string | undefined;
  const collapsedIcon = slotProps.collapsedIcon as IconProps['name'] | undefined;
  const collapsedItems = slotProps.collapsedItems as ButtonGroupCollapsedItem[] | undefined;
  const order = slotProps.collapsedOrder as number | undefined;
  const action = findFirstElement(slotProps.children as ReactNode, [Button, IconButton, DropdownMenu]);

  if (action?.type === IconButton) {
    const actionProps = action.props as { name?: IconProps['name']; label?: string; onClick?: () => void; disabled?: boolean };
    const label = collapsedLabel ?? actionProps.label;
    if (!label) return null;
    return {
      label,
      icon: collapsedIcon ?? actionProps.name,
      onSelect: actionProps.onClick,
      disabled: actionProps.disabled,
      order,
    };
  }

  if (action?.type === Button) {
    const actionProps = action.props as { children?: ReactNode; onClick?: () => void; disabled?: boolean };
    const label = collapsedLabel ?? (typeof actionProps.children === 'string' ? actionProps.children : undefined);
    if (!label) return null;
    return {
      label,
      icon: collapsedIcon,
      onSelect: actionProps.onClick,
      disabled: actionProps.disabled,
      order,
    };
  }

  // A `DropdownMenu`-wrapped action: its live menu content isn't introspected, so `collapsedItems` is
  // required for a submenu — omit the action entirely (rather than render a dead menu item) when it's missing.
  if (action?.type === DropdownMenu && !collapsedItems) return null;

  // A submenu also needs `collapsedLabel` for its `SubTrigger` — omit rather than render one with no name.
  if (collapsedItems) {
    if (!collapsedLabel) return null;
    const submenuProps = slotProps.collapsedSubmenuProps as CollapsedAction['submenuProps'];
    return { label: collapsedLabel, icon: collapsedIcon, subItems: collapsedItems, submenuProps, order };
  }

  return collapsedLabel ? { label: collapsedLabel, icon: collapsedIcon, order } : null;
}
